///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2025
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useEffect } from "react";

import { controls } from "../../../helpers/types";
import navigate, { getElement } from "../../../helpers/navigate";
import addHighlightRowClass from "../../../helpers/addHighlightRowClass";
import removeHighlightRowClass from "../../../helpers/removeHighlightRowClass";
import addTitleForTruncatedText from "../../../helpers/addTitleForTruncatedText";
import { boxHighlight } from "../../../helpers/tableSelection";
import DropdownForGrid from "../../form-controls/DropdownForGrid";

export const Cell = ({
  value,
  cellDetails,
  handleChange,
  changed,
  rowIndex,
  cellIndex,
  columnCount,
  onMouseDown,
  onMouseEnter,
  onClick,
  isLaneMatch = false,
  rowCount,
  disabled,
  lane,
  ...rest
}) => {
  const format = cellDetails.format;
  const displayDetails =
    lane != null && cellDetails.resolveCellDisplay
      ? { ...cellDetails, ...cellDetails.resolveCellDisplay(lane) }
      : cellDetails;

  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    if (isFocused) {
      let currentElement = getElement(isLaneMatch, rowIndex, cellIndex);
      currentElement && currentElement.select();
    }
  }, [isFocused, rowIndex, cellIndex, isLaneMatch]);

  const [stateValue, setValue] = useState(format ? format(value) : value);

  useEffect(() => {
    setValue(format ? format(value) : value);
    if (window.startCell && window.endCell) {
      boxHighlight();
    }
  }, [setValue, value, format]);

  let textValue = stateValue || stateValue === 0 ? stateValue : "";
  if (
    textValue !== "" &&
    textValue !== null &&
    textValue !== undefined &&
    !isNaN(textValue) &&
    !isFocused &&
    displayDetails.cellUnit
  ) {
    if (displayDetails.intPercent) {
      textValue = textValue ? parseInt(textValue) : textValue;
    } else {
      textValue = parseFloat(textValue);
      textValue = (Math.round(textValue * 100) / 100).toFixed(2);
    }
  }
  if (
    !isFocused &&
    textValue !== "" &&
    textValue !== null &&
    textValue !== undefined &&
    (textValue || textValue === 0) &&
    displayDetails.cellUnit
  ) {
    textValue = displayDetails.unitLeft
      ? displayDetails.cellUnit + textValue
      : textValue + displayDetails.cellUnit;
  }

  const handleValidate = ({ target }) => {
    if (cellDetails.pattern && target.value) {
      target.value.match(cellDetails.pattern) && setValue(target.value);
    } else {
      setValue(target.value);
    }
  };

  const handleBlur = () => {
    removeHighlightRowClass(rowIndex, cellIndex, isLaneMatch);
    setIsFocused(false);
    if (format ? format(value) !== stateValue : value !== stateValue) {
      if (cellDetails.parse) {
        let parseDetails = null;
        if (cellDetails.validationBetweenMinMaxValues) {
          parseDetails = cellDetails.parse(
            stateValue,
            cellDetails.minValue,
            cellDetails.maxValue
          );
        } else {
          parseDetails = cellDetails.parse(stateValue);
        }
        if (parseDetails?.value !== value && parseDetails?.isValid) {
          handleChange(parseDetails.value);
        } else {
          setValue(value);
        }
      } else {
        handleChange(stateValue);
      }
    }
  };

  const handleKeyDown = event =>
    navigate(event, rowIndex, cellIndex, columnCount, isLaneMatch, rowCount);

  const handleFocus = ({ target }) => {
    addHighlightRowClass(target);
    if (
      cellDetails.control === controls.textInput ||
      cellDetails.control === controls.dropdown
    ) {
      setIsFocused(true);
    }

    if (!displayDetails.cellUnit) {
      target.select();
    }
  };

  return (
    <td
      className={`${
        cellDetails.control === controls.checkbox
          ? "simple-cell text-center"
          : "pricing-cell"
      } ${changed ? "font-bold" : ""}`}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {cellDetails.control === controls.checkbox && (
        <input
          id={rowIndex + "_" + cellIndex}
          onKeyDown={handleKeyDown}
          className="pointer"
          type="checkbox"
          onChange={event =>
            window.endCell || handleChange(event.target.checked)
          }
          checked={value === true}
          disabled={cellDetails.disabled === true || disabled}
          onFocus={handleFocus}
          onBlur={() =>
            removeHighlightRowClass(rowIndex, cellIndex, isLaneMatch)
          }
        />
      )}
      {cellDetails.control === controls.textInput && (
        <input
          id={rowIndex + "_" + cellIndex}
          onKeyDown={handleKeyDown}
          className={`${cellDetails.right && "text-right"}`}
          onChange={handleValidate}
          type="text"
          value={textValue}
          onBlur={handleBlur}
          pattern={cellDetails.pattern}
          autoComplete="off"
          onFocus={handleFocus}
          onMouseEnter={e => addTitleForTruncatedText(e.target, textValue)}
          onMouseLeave={e => (e.target.title = "")}
          onScroll={e => e.stopPropagation()}
          onDragStart={e => e.preventDefault()}
          {...rest}
        />
      )}
      {cellDetails.control === controls.dropdown && (
        <DropdownForGrid
          inputId={rowIndex + "_" + cellIndex}
          value={cellDetails.options.find(x => x.value === value)}
          staticOptions={cellDetails.options}
          handleChange={o => handleChange(o.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onMouseEnter={e =>
            addTitleForTruncatedText(
              e.target,
              cellDetails.options.find(x => x.value === value)?.label
            )
          }
          onMouseLeave={e => (e.target.title = "")}
          onScroll={e => e.stopPropagation()}
          onDragStart={e => e.preventDefault()}
        />
      )}
    </td>
  );
};

export default Cell;
